import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { platformAPI, getIpeWorkspaceMode } from '@/utils/api';
import { showToast } from '@/utils/toast';
import {
  TIKTOK_ACTIVE_SHOP_STORAGE_KEY,
  isTiktokShopAuthorized,
  normalizeTiktokShopType,
  shopTypeLabelForSlug as labelForTiktokShopTypeSlug,
} from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokWorkspaceAuthOptional } from '@/tiktok-platform/context/TiktokWorkspaceAuthContext';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';

const TiktokShopContext = createContext(null);

function readStoredShopId() {
  if (typeof localStorage === 'undefined') return '';
  try {
    return localStorage.getItem(TIKTOK_ACTIVE_SHOP_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function readUrlShopId() {
  if (typeof window === 'undefined') return '';
  try {
    const id = new URLSearchParams(window.location.search).get('shopId');
    return isConcreteShopId(id) ? String(id) : '';
  } catch {
    return '';
  }
}

function persistShopId(shopId) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (shopId) {
      localStorage.setItem(TIKTOK_ACTIVE_SHOP_STORAGE_KEY, String(shopId));
    } else {
      localStorage.removeItem(TIKTOK_ACTIVE_SHOP_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function shopTypeEqualsTiktok(a, b) {
  return normalizeTiktokShopType(a) === normalizeTiktokShopType(b);
}

function useWorkspaceModeKey() {
  const [mode, setMode] = useState(() => getIpeWorkspaceMode());
  useEffect(() => {
    const sync = (ev) => {
      const next = ev?.detail?.mode;
      if (next === 'personal' || next === 'team') {
        setMode(next);
        return;
      }
      setMode(getIpeWorkspaceMode());
    };
    window.addEventListener('ipe:workspace-mode-changed', sync);
    window.addEventListener('ipe-workspace-mode-changed', sync);
    window.addEventListener('ipe:workspace-refresh', sync);
    return () => {
      window.removeEventListener('ipe:workspace-mode-changed', sync);
      window.removeEventListener('ipe-workspace-mode-changed', sync);
      window.removeEventListener('ipe:workspace-refresh', sync);
    };
  }, []);
  return mode;
}

export function TiktokShopProvider({ children }) {
  const { shopType: shopTypeSlug = 'semi' } = useParams();
  const workspaceAuth = useTiktokWorkspaceAuthOptional();
  const workspaceMode = useWorkspaceModeKey();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeShopId, setActiveShopId] = useState(() => readUrlShopId() || readStoredShopId());

  const expectedType = useMemo(() => {
    const fromSlug = labelForTiktokShopTypeSlug(shopTypeSlug);
    if (fromSlug) return fromSlug;
    return normalizeTiktokShopType(workspaceAuth?.workspaceShopType) || '';
  }, [shopTypeSlug, workspaceAuth?.workspaceShopType]);

  const scopedShops = useMemo(() => {
    const list = (shops || []).filter((s) => isTiktokShopAuthorized(s));
    if (!expectedType) return list;
    return list.filter((s) => shopTypeEqualsTiktok(s.shopType, expectedType));
  }, [shops, expectedType]);

  const canUseAllShops = scopedShops.length >= 2;

  const loadShops = useCallback(async () => {
    setLoading(true);
    try {
      const list = await platformAPI.listShops();
      const tiktokShops = (Array.isArray(list) ? list : []).filter(
        (s) => String(s.platformCode || '').toUpperCase() === 'TIKTOK',
      );
      setShops(tiktokShops);
      const storedShopId = readStoredShopId();
      const urlShopId = readUrlShopId();
      const authorizedOfType = tiktokShops.filter((s) => {
        if (!isTiktokShopAuthorized(s)) return false;
        if (!expectedType) return true;
        return shopTypeEqualsTiktok(s.shopType, expectedType);
      });
      setActiveShopId((prev) => {
        const candidates = [urlShopId, storedShopId, prev].filter(Boolean);
        for (const candidate of candidates) {
          if (isAllShopsScope(candidate)) {
            if (authorizedOfType.length >= 2) {
              persistShopId(PLATFORM_ALL_SHOPS_ID);
              return PLATFORM_ALL_SHOPS_ID;
            }
            continue;
          }
          const found = tiktokShops.find((s) => String(s.id) === String(candidate));
          if (found && isTiktokShopAuthorized(found)) {
            if (expectedType && !shopTypeEqualsTiktok(found.shopType, expectedType)) {
              continue;
            }
            persistShopId(String(candidate));
            return String(candidate);
          }
        }
        if (!tiktokShops.length && (storedShopId || prev)) {
          return String(storedShopId || prev);
        }
        const sameType = tiktokShops.find((s) => (
          isTiktokShopAuthorized(s)
          && (!expectedType || shopTypeEqualsTiktok(s.shopType, expectedType))
        ));
        if (sameType) {
          persistShopId(String(sameType.id));
          return String(sameType.id);
        }
        persistShopId('');
        return '';
      });
    } catch (err) {
      setShops([]);
      const msg = String(err?.message || '').trim();
      if (msg) showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [expectedType]);

  useEffect(() => {
    loadShops();
  }, [loadShops, workspaceMode]);

  const switchToAll = useCallback(() => {
    if (scopedShops.length < 2) {
      return { ok: false, message: fixedUi('同类型已授权店铺不足 2 家，无法进入全部视图') };
    }
    if (isAllShopsScope(activeShopId)) {
      return { ok: true, unchanged: true };
    }
    setActiveShopId(PLATFORM_ALL_SHOPS_ID);
    persistShopId(PLATFORM_ALL_SHOPS_ID);
    return { ok: true, all: true };
  }, [scopedShops.length, activeShopId]);

  const switchShop = useCallback((shopId) => {
    if (isAllShopsScope(shopId)) {
      return switchToAll();
    }
    const shop = shops.find((s) => String(s.id) === String(shopId));
    if (!shop) {
      return { ok: false, message: fixedUi('店铺不存在') };
    }
    if (!isTiktokShopAuthorized(shop)) {
      return { ok: false, message: fixedUi('只能切换已授权的店铺') };
    }
    const current = shops.find((s) => String(s.id) === String(activeShopId));
    const currentType = normalizeTiktokShopType(current?.shopType)
      || expectedType
      || fixedUi('半托管');
    if (!shopTypeEqualsTiktok(shop.shopType, currentType)) {
      const targetType = normalizeTiktokShopType(shop.shopType) || fixedUi('未知');
      return {
        ok: false,
        message: fixedUiTpl(
          '只能切换同类型店铺（当前「{0}」，目标「{1}」）。跨类型请返回门禁重进。',
          currentType,
          targetType,
        ),
      };
    }
    const next = String(shopId);
    if (next === String(activeShopId)) {
      return { ok: true, unchanged: true, shop };
    }
    setActiveShopId(next);
    persistShopId(next);
    return { ok: true, shop };
  }, [shops, activeShopId, expectedType, switchToAll]);

  const isAllShopsMode = isAllShopsScope(activeShopId);

  const activeShop = useMemo(
    () => (isAllShopsMode
      ? null
      : (shops.find((s) => String(s.id) === String(activeShopId)) || null)),
    [shops, activeShopId, isAllShopsMode],
  );

  const value = useMemo(
    () => ({
      shops,
      scopedShops,
      canUseAllShops,
      activeShopId,
      activeShop,
      isAllShopsMode,
      expectedShopType: expectedType,
      loading,
      switchShop,
      switchToAll,
      reloadShops: loadShops,
    }),
    [
      shops,
      scopedShops,
      canUseAllShops,
      activeShopId,
      activeShop,
      isAllShopsMode,
      expectedType,
      loading,
      switchShop,
      switchToAll,
      loadShops,
    ],
  );

  return (
    <TiktokShopContext.Provider value={value}>
      {children}
    </TiktokShopContext.Provider>
  );
}

export function useTiktokShop() {
  const ctx = useContext(TiktokShopContext);
  if (!ctx) {
    throw new Error('useTiktokShop must be used within TiktokShopProvider');
  }
  return ctx;
}

export function useTiktokShopOptional() {
  return useContext(TiktokShopContext);
}
